import { BaseViewStore, HyperlinkCellData, ImageCellData } from 'core/stores';
import { ExitScanModeForm } from 'modules/order/forms';
import PalletStore from './PalletStore';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import { ApplicationStore } from 'platform/application/stores';
import { formInvalidate, isSeller, isSmartshipWarehouseAdministrator, isSuperAdminOrganization, isWarehouse } from 'core/utils';
import { SoundService } from 'modules/sound/services';
import { action, observable, makeObservable } from 'mobx';
import { sourceOfChange } from 'common/constants';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';

class ExitScanModeViewStore extends BaseViewStore {
	@observable exitScanNotification = null;
	@observable fulfillmentOrders = [];

	form = new ExitScanModeForm();

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.soundService = new SoundService();
		this.palletStore = new PalletStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.organizationStore = new ApplicationStore(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.isSeller = isSeller(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isSmartshipWarehouseAdministrator = isSmartshipWarehouseAdministrator(rootStore);
		this.defineTableData();
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		await this.initializeDropdowns();
		this.resume();
	};

	initializeDropdowns = async () => {
		this.palletDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('palletId'),
			store: new PalletStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'barcode',
			},
			fetchInitialValues: this.isWarehouse,
			filterProperties: {
				isOpened: true,
				isSeller: !this.isWarehouse,
				embed: [
					'shipmentInfos',
					'shipmentInfos.fulfillmentOrder',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderStatus',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems.product',
					'carrierService',
					'carrierService.carrierServiceType',
					'carrierTypeServiceType',
				],
				warehouseIds:
					(!this.isWarehouse) && this.selectedWarehouse
						? [this.selectedWarehouse.value]
						: undefined,
			},
			filterCallback: async options => await this.palletFilterCallback(options),
			onChangeCallback: this.setPalletOnSearch,
		});
		this.warehouseDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('warehouseId'),
			store: new ApplicationStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
			fetchInitialValues: !this.isWarehouse,
			filterProperties: {
				warehouseOnly: true,
				isOrganizationActive: true,
			},
			onChangeCallback: value => this.onWarehouseChange(value),
		});
	};

	@action.bound
	palletFilterCallback = async options => {
		const inputRefValue = _.get(this.palletDropdownStore, 'reference.select.inputRef.value');
		if (inputRefValue && inputRefValue != '') {
			if (options.length == 1) {
				// setValue fires onChangeCallback, which runs setPalletOnSearch. Calling it again here scans twice.
				this.palletDropdownStore.setValue(options[0]);
			} else if (options.length == 0) {
				this.exitScanNotification = 'FULFILLMENTORDER.EXITSCANMODE.NOTIFICATION.PALLETNOTFOUND';
				this.fulfillmentOrders = [];
				await this.soundService.error();
			}
			this.palletDropdownStore.reference.select.setState({ menuIsOpen: false, inputValue: '' });
		}
		return options.map(item => {
			return { value: item.id, label: item.barcode, extra: item };
		});
	};

	@action.bound
	exitScan = async () => {
		this.suspend();
		try {
			this.exitScanNotification = null;
			this.fulfillmentOrders = [];
			let { extra, value } = this.palletDropdownStore.value;
			await this.palletStore.exitScan({ id: value }, sourceOfChange.exitScanMode);
			this.transformData(extra);
			this.exitScanNotification = 'FULFILLMENTORDER.EXITSCANMODE.NOTIFICATION.SUCCESS';
			setTimeout(() => {
				if (this.form.$('palletId')) {
					this.form.$('palletId').clear();
					this.palletDropdownStore.clear();
				}
			}, 2000);
			await this.palletDropdownStore.reload();
			await this.soundService.success();
		} catch (err) {
			if (err.statusCode === 400 && err.data && err.data.message) {
				this.exitScanNotification = err.data.message;
				await this.soundService.error();
			} else {
				formInvalidate({ form: this.form, err });
			}
		}
		this.resume();
		this.modalParams.onClose = this.modalParams.data.exitScanCallback;
	};

	setPalletOnSearch = async () => {
		this.fulfillmentOrders = [];
		if (this.palletDropdownStore.value) {
			this.exitScanNotification = null;
			await this.exitScan();
		}
	};

	@action.bound
	onWarehouseChange = async item => {
		this.fulfillmentOrders = [];
		if (this.palletDropdownStore.value) this.palletDropdownStore.clear();
		this.exitScanNotification = null;
		await this.palletDropdownStore.updateFilter({
			warehouseIds:
				(!this.isWarehouse) && item ? [this.form.$('warehouseId').value] : undefined,
		});
	};

	@action.bound
	defineTableData = () => {
		this.tableData = {
			columns: [
				{
					key: 'fulfillmentOrderId',
					title: 'FULFILLMENTORDER.EXITSCANMODE.TABLEDATA.FULFILLMENTORDERID',
					dataCell: HyperlinkCellData,
					routeName: 'master.app.main.order.list.detail',
					selected: true,
					width: 85,
					filter: null,
				},
				{
					key: 'fulfillmentOrderStatus.name',
					title: 'FULFILLMENTORDER.EXITSCANMODE.TABLEDATA.STATUS',
					selected: true,
					width: 120,
					filter: null,
				},
				{
					key: 'product.sku',
					title: 'FULFILLMENTORDER.EXITSCANMODE.TABLEDATA.PRODUCTSKU',
					selected: true,
					width: 150,
					filter: null,
				},
				{
					key: 'productImage',
					title: 'FULFILLMENTORDER.EXITSCANMODE.TABLEDATA.IMAGE',
					dataCell: ImageCellData,
					selected: true,
					width: 175,
					filter: null,
				},
			],
			actions: {},
			sortObject: [],
		};
	};

	@action.bound
	transformData = data => {
		let product = {};
		let fulfillmentOrder = {};
		_.forEach(data.shipmentInfos, item => {
			if (item.fulfillmentOrder.fulfillmentOrderItems.length != 1) {
				product.name = 'Multiple';
				product.sku = 'Multiple';
			} else {
				product.name = item.fulfillmentOrder.fulfillmentOrderItems[0].product.name;
				product.sku = item.fulfillmentOrder.fulfillmentOrderItems[0].product.mainSku;
			}
			fulfillmentOrder = item.fulfillmentOrder;
			fulfillmentOrder.fulfillmentOrderStatus.name = 'Exit Scan';
			fulfillmentOrder.product = product;
			this.fulfillmentOrders.push(fulfillmentOrder);
			product = {};
			fulfillmentOrder = {};
		});
	};

	onDetails = () => {
		this.rootStore.routerStore.goTo('master.app.main.order.exitscanmode.detail', {
			id: this.palletDropdownStore.value.extra.id,
		});

		this.modalParams.close();
	};

	openDetailPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.list.detail', {
			id: id,
		});
	};
}

export default ExitScanModeViewStore;
